import axios from 'axios'
import {clearSession, getSessionWithCrypto, sessionName, setSessionWithCrypto} from "@/utils/Session"
import { PAYER } from "@/config";
import { toastError, toastErrors } from "@/utils/ToastUtil";
import AuthComponent from "@/components/api/AuthComponent";

axios.defaults.baseURL = `${PAYER.BASE_URL}${PAYER.BASE_PATH}`;

// Флаг для предотвращения множественных редиректов
let isRedirecting = false;

// Флаг для отслеживания процесса обновления токена
let isRefreshing = false;

// Очередь запросов, ожидаюих обновления токена
let failedQueue = [];

// Обработка очереди запросов
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const handleAuthError = (message = "Ошибка авторизации") => {
    if (isRedirecting) {
        return
    }
    isRedirecting = true;

    clearSession()
    sessionStorage.clear();
    localStorage.clear();

    toastError(message);

    setTimeout(() => {
        window.location.href = '/'
    }, 500)
};

const showErrorNotification = (error, config) => {
    if (config?.skipErrorNotification) return;

    if (error.response?.data?.errors) {
        toastErrors(error.response.data.errors);
    } else if (error.message) {
        toastError(error.message);
    } else {
        toastError("Произошла неизвестная ошибка");
    }
};

// Функция обновления токена
const refreshAuthToken = async () => {
    const credential = await getSessionWithCrypto(sessionName.CREDENTIAL)
    if (!credential || !credential.refreshToken) {
        throw new Error('No refresh token available');
    }
    const response = await AuthComponent.tokens({
        refreshToken: credential.refreshToken,
        accessToken: credential.accessToken
    });
    if (response.status === 200) {
        const newCredential = response.data.result;
        await setSessionWithCrypto(sessionName.CREDENTIAL, newCredential);
        return newCredential;
    }
    throw new Error('Token refresh failed');
};

axios.interceptors.request.use(async (config) => {
    try {
        const credential = await getSessionWithCrypto(sessionName.CREDENTIAL)
        config.headers.Authorization = credential ? `${credential.type} ${credential.accessToken}` : ""
        config.timeout = 10000
        return config;
    } catch (error) {
        console.error("Error in request interceptor: ",error)
        return Promise.reject(error);
    }

});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if (!error.response) {
            // Сетевая ошибка
            showErrorNotification(new Error("Сетевая ошибка. Проверьте подключение к интернету"), error.config);
            return Promise.reject(error);
        }

        const status = error.response.status;

        // Обработка 401 ошибки с попыткой refresh token
        if (status === 401 && !originalRequest._retry) {
            // Не пытаемся обновить токен при неудачном логине
            if (originalRequest.url?.includes('/auth/login')) {
                showErrorNotification(new Error("Неверный логин или пароль"), originalRequest);
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Если уже идёт процесс обновления, добавляем запрос в очередь
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `${token.type} ${token.accessToken}`;
                        return axios(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newCredential = await refreshAuthToken();
                processQueue(null, newCredential);

                originalRequest.headers.Authorization = `${newCredential.type} ${newCredential.accessToken}`;
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                handleAuthError("Сессия истекла. Пожалуйста, войдите снова");
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        switch (status) {
            case 400:
                // Ошибка валидации
                showErrorNotification(error, originalRequest);
                return Promise.reject(error);

            case 401:
                // Неавторизованный доступ
                handleAuthError("Сессия истекла. Пожалуйста, войдите снова");
                break;

            case 403:
                // Доступ запрещен
                handleAuthError("Недостаточно прав для выполнения действия");
                break;

            case 404:
                // Ресурс не найден
                showErrorNotification(new Error("Запрашиваемый ресурс не найден"), originalRequest);
                return Promise.reject(error);

            case 405:
                // Метод не разрешен
                showErrorNotification(new Error("Метод не разрешен"), originalRequest);
                return Promise.reject(error);

            case 408:
                // Таймаут запроса
                showErrorNotification(new Error("Время запроса истекло"), originalRequest);
                return Promise.reject(error);

            case 500:
                // Внутренняя ошибка сервера
                showErrorNotification(new Error("Внутренняя ошибка сервера"), originalRequest);
                return Promise.reject(error);

            default:
                // Остальные ошибки
                if (status >= 500) {
                    showErrorNotification(new Error("Ошибка сервера"), originalRequest);
                } else {
                    showErrorNotification(error, originalRequest);
                }
                return Promise.reject(error);
        }
    },
)


export default axios